import {
  Check,
  LayoutGrid,
  List,
  ListFilter,
  Sparkle,
  XIcon,
} from "lucide-react";
import { Button } from "../ui/button";
import React, {
  useEffect,
  useCallback,
  useState,
  useRef,
  useMemo,
} from "react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ComponentAssetCard } from "@/components/Community/ComponentAssetCard";
import { useComponentsQuery } from "@/hooks/useComponents";
import {
  CommunityPanelSections,
  ViewMode,
  useCommunityPanelStore,
} from "@/store/zustand/communityPanelStore";
import {
  LayoutOption,
  ComponentAssetType,
  ComponentType,
  AuthorType,
  TagType,
} from "@/utils/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { CommunityFilterPopover } from "./CommunityFilterPopover";
import { getItemsBySubcategory, getAllItems } from "@/utils/headingsToTagsMap";
import CategoryView from "./CategoryView";
import FeaturedView from "./FeaturedView";
import UIKitsView from "./UIKitsView";
import _ from "lodash";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { SearchInput } from "@/components/ui/search-input";

// Layout options sourced from headingsToTagsMap
const applicationShellItems = getItemsBySubcategory(
  "Application UI",
  "Application Shells",
);
const layoutOptions: LayoutOption[] = applicationShellItems.map((item) => ({
  id: item.id,
  name: item.displayName, // Map displayName to name
  icon: item.url || "", // Map url to icon, fallback to empty string if null
}));

// AKA reactcomponents panel
interface Section {
  id: number;
  name: string;
  icon?: React.ReactNode;
}

const sections: Section[] = [
  {
    id: CommunityPanelSections.Featured,
    name: "Featured",
  },
  {
    id: CommunityPanelSections.UIKits,
    name: "UI Kits",
  },
  {
    id: CommunityPanelSections.Categories,
    name: "Categories",
  },
];

// Filter tag component for reusability
interface FilterTagProps {
  label: string;
  onRemove: () => void;
}

const FilterTag: React.FC<FilterTagProps> = ({ label, onRemove }) => (
  <div
    onClick={onRemove}
    className="flex flex-row items-center gap-1 px-2 h-5 bg-[#2F3035] rounded text-2xs text-secondary-n-50 cursor-pointer hover:bg-[#3F4045] select-none capitalize"
  >
    <span className="truncate">{label}</span>
    <XIcon className="w-3 h-3 group-hover:text-secondary-n-50" />
  </div>
);

const FilterTags = () => {
  const {
    selectedCollections,
    collections,
    selectedLayout,
    selectedTags,
    toggleCollection,
    setSelectedLayout,
    allCollectionsForFilter,
    selectedCategory,
    setSelectedCategory,
    toggleTag,
  } = useCommunityPanelStore();

  const allTagItems = getAllItems();

  // Get collection names from their IDs
  const getCollectionName = (id: string) => {
    const collectionFromFilter = allCollectionsForFilter.find(
      (c) => c.id.toString() === id,
    );
    if (collectionFromFilter) return collectionFromFilter.name;
    const collection = collections.find((c) => c.id.toString() === id);
    if (collection) return collection.name;
    console.warn(`Collection with ID ${id} not found in any data source`);
    return id;
  };

  const getLayoutName = (layoutId: string) => {
    const layout = layoutOptions.find((l) => l.id === layoutId);
    return layout?.name || layoutId;
  };

  const getTagName = (tagId: string) => {
    const tag = allTagItems.find((t) => t.id === tagId);
    return tag?.displayName || tagId;
  };

  // Combine selectedCategory and selectedTags for display, ensuring uniqueness
  const displayedTags = new Set(selectedTags);
  if (selectedCategory) {
    displayedTags.add(selectedCategory);
  }

  return (
    <div className="flex flex-wrap gap-2">
      {/* Collections */}
      {selectedCollections.map((collectionId) => (
        <FilterTag
          key={`col-${collectionId}`}
          label={getCollectionName(collectionId)}
          onRemove={() => toggleCollection(collectionId)}
        />
      ))}

      {/* Layout */}
      {selectedLayout && (
        <FilterTag
          key={`layout-${selectedLayout}`}
          label={getLayoutName(selectedLayout)}
          onRemove={() => setSelectedLayout(null)}
        />
      )}

      {/* Combined Tags */}
      {Array.from(displayedTags).map((tagId) => (
        <FilterTag
          key={`tag-${tagId}`}
          label={getTagName(tagId)}
          onRemove={() => {
            // Remove from both single and multi select state if present
            if (selectedCategory === tagId) {
              setSelectedCategory(null);
            }
            if (selectedTags.includes(tagId)) {
              toggleTag(tagId);
            }
          }}
        />
      ))}
    </div>
  );
};

const CommunityPanel = React.memo(
  ({ isActiveTab }: { isActiveTab: boolean }) => {
    const {
      searchVal,
      setSearchVal,
      selectedSection,
      setSelectedSection,
      viewMode,
      toggleViewMode,
      components,
      setComponents,
      collections,
      setCollections,
      selectedCategory,
      setSelectedCategory,
      selectedLayout,
      setSelectedLayout,
      searchTrigger,
      incrementSearchTrigger,
      page,
      setPage,
      hasMore,
      setHasMore,
      isLoading,
      setIsLoading,
      componentCounts,
      setComponentCounts,
      selectedCollections,
      selectedTags,
      isFilterPopoverOpen,
      setSelectedCollections,
      allCollectionsForFilter,
    } = useCommunityPanelStore();

    // Add local state for the left panel filter
    const [leftPanelFilter, setLeftPanelFilter] = useState("");

    const {
      fetchComponents: fetchComponentsQuery,
      getComponentCounts,
      fetchCollections,
    } = useComponentsQuery();
    const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

    const pendingApprovalEnabled = useFeatureFlagEnabled(
      "pending_approval_reactcomponents",
    );

    const convertToAssetComponent = useCallback(
      (
        component: ComponentType & { authors?: AuthorType; tags?: TagType[] },
      ): ComponentAssetType => {
        return {
          id: component.id,
          name: component.name,
          description: component.description || undefined,
          image_url: component.image_url || undefined,
          authors: component.authors
            ? [
                {
                  first_name: component.authors.first_name,
                  last_name: component.authors.last_name,
                },
              ]
            : undefined,
          tags: component.tags?.map((tag) => ({ name: tag.name })) || undefined,
          npm_dependencies: component.npm_dependencies as
            | Record<string, string>
            | undefined,
        };
      },
      [],
    );

    const rightPanelRef = useRef<HTMLDivElement>(null);
    const componentsRef = useRef<ComponentAssetType[]>(components);
    // Update the ref whenever components changes
    useEffect(() => {
      componentsRef.current = components;
    }, [components]);

    const handleFilterChange = useCallback(() => {
      setPage(0);
      setHasMore(true);
      incrementSearchTrigger();
    }, [incrementSearchTrigger, setPage, setHasMore]);

    const debouncedHandleFilterChange = useCallback(
      _.debounce(() => {
        handleFilterChange();
      }, 500),
      [handleFilterChange],
    );

    // Handle search input changes
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchVal(e.target.value);
      debouncedHandleFilterChange();
    };

    // Clear search input
    const handleClearSearch = () => {
      setSearchVal("");
      handleFilterChange();
    };

    useEffect(() => {
      const fetchCollectionsData = async () => {
        if (!fetchCollections || !isActiveTab) return;
        const { data } = await fetchCollections();
        if (data) {
          setCollections(
            data.map((collection) => ({
              ...collection,
              components_length: [
                {
                  count: componentCounts.collections[collection.id] || 0,
                },
              ],
            })),
          );
        }
      };
      if (isActiveTab) {
        fetchCollectionsData();
      }
    }, [isActiveTab, fetchCollections]);

    useEffect(() => {
      const fetchDisplayCounts = async () => {
        if (!isActiveTab || !getComponentCounts || !supabaseClient) return;

        // Define tags for fetching general DISPLAY counts (Layouts, Tags)
        // Only includes popover tags. Excludes selectedLayout and selectedTag.
        const tagsForGeneralCountsSet = new Set(selectedTags);
        const finalTagsForGeneralCounts = Array.from(tagsForGeneralCountsSet);

        // Define tags for fetching individual UI Kit counts
        // Includes popover tags AND the selectedLayout. Excludes selectedTag.
        const tagsForKitCountsSet = new Set(selectedTags);
        if (selectedLayout) {
          tagsForKitCountsSet.add(selectedLayout);
        }
        const finalTagsForKitCounts = Array.from(tagsForKitCountsSet);

        try {
          // 1. Fetch counts for individual collections (UI Kits)
          // These counts respect search, layout, and popover tags.
          const collectionCountMap: Record<string, number> = {};
          const collectionsToCount =
            allCollectionsForFilter.length > 0
              ? allCollectionsForFilter
              : collections;
          for (const collection of collectionsToCount) {
            const collectionId = collection.id.toString();
            // Fetch count for this specific kit, considering popover tags AND layout
            const counts = await getComponentCounts({
              searchQuery: searchVal,
              isPublic: true,
              deleted: false,
              selectedCollections: [collectionId], // Count for this specific kit
              currentTags: finalTagsForKitCounts, // Respect layout & popover tags
            });
            if (counts && counts.collections) {
              collectionCountMap[collectionId] =
                counts.collections[collectionId] || 0;
            } else {
              collectionCountMap[collectionId] = 0;
            }
          }

          // 2. Fetch general counts for Layouts and Tags (for Filter Popover etc.)
          // These counts ignore the selected collection(s) AND selectedLayout, mirroring category behavior.
          const generalCounts = await getComponentCounts({
            searchQuery: searchVal,
            isPublic: true,
            deleted: false,
            selectedCollections: [],
            currentTags: finalTagsForGeneralCounts,
          });

          // 3. Set state
          setComponentCounts({
            collections: collectionCountMap, // From individual fetches (respects layout/popover tags)
            tags: generalCounts?.tags || {}, // From general fetch (ignores selected collection/layout)
          });
        } catch (error) {
          console.error("Error fetching component display counts:", error);
          setComponentCounts({ collections: {}, tags: {} });
        }
      };

      if (isActiveTab) {
        fetchDisplayCounts();
      }
    }, [
      isActiveTab,
      getComponentCounts,
      supabaseClient,
      searchTrigger,
      selectedTags,
      selectedLayout,
      collections,
      allCollectionsForFilter,
      setComponentCounts,
    ]);

    // Fetch components (this uses ALL filters, including selectedTag)
    const fetchComponents = useCallback(
      async (isInitialLoad = true) => {
        if (!supabaseClient || !fetchComponentsQuery) return;
        setIsLoading(true);

        const searchVal = useCommunityPanelStore.getState().searchVal;
        const page = useCommunityPanelStore.getState().page;

        try {
          if (isInitialLoad) {
            setHasMore(true);
          }

          const start = isInitialLoad ? 0 : page * 20;
          const end = start + 19;

          // Combine all active tags for filtering
          const combinedTags = new Set(selectedTags);
          if (selectedCategory) {
            combinedTags.add(selectedCategory);
          } else if (selectedLayout) {
            combinedTags.add(selectedLayout);
          }
          const finalCurrentTags = Array.from(combinedTags);

          const queryOptions = {
            select: "*, authors(*), tags(*)",
            isPublic: true,
            sortBy: "-created_at",
            searchQuery: searchVal,
            range: [start, end] as [number, number],
            selectedCollections: selectedCollections,
            currentTags: finalCurrentTags,
          };

          const { data: fetchedComponents, error } =
            await fetchComponentsQuery(queryOptions);

          if (error) throw error;

          if (fetchedComponents && Array.isArray(fetchedComponents)) {
            const assetComponents = fetchedComponents.map(
              convertToAssetComponent,
            );
            if (isInitialLoad) {
              setComponents(assetComponents);
            } else {
              if (assetComponents.length > 0) {
                setComponents([...componentsRef.current, ...assetComponents]);
                setPage(page + 1);
              }
            }
            setHasMore(assetComponents.length === 20);
          } else {
            if (isInitialLoad) setComponents([]);
            setHasMore(false);
          }
        } catch (error: any) {
          console.error(
            `Error ${isInitialLoad ? "loading" : "loading more"} components:`,
            error,
          );
          toast.error(
            `Failed to ${isInitialLoad ? "load" : "load more"} components: ${
              error.message || "Unknown error"
            }`,
          );
          if (isInitialLoad) setComponents([]);
          setHasMore(false);
        } finally {
          setIsLoading(false);
        }
      },
      [
        supabaseClient,
        fetchComponentsQuery,
        selectedCollections,
        selectedCategory,
        selectedLayout,
        convertToAssetComponent,
        componentsRef,
        setIsLoading,
        setPage,
        setHasMore,
        setComponents,
        selectedLayout,
        selectedCategory,
        selectedTags,
      ],
    );

    // Fetch components when filters/search change (triggered by searchTrigger)
    useEffect(() => {
      if (isActiveTab) {
        fetchComponents(true);
      }
    }, [isActiveTab, fetchComponents, searchTrigger]);

    // Optimize scroll event listener with throttling
    useEffect(() => {
      const handleScroll = _.throttle(() => {
        if (!rightPanelRef.current) return;

        const { scrollTop, scrollHeight, clientHeight } = rightPanelRef.current;

        // Check if we're near the bottom (within 200px) and we have more components to load
        if (
          scrollHeight - scrollTop - clientHeight < 200 &&
          !isLoading &&
          hasMore
        ) {
          fetchComponents(false);
        }
      }, 200);

      const rightPanel = rightPanelRef.current;
      if (rightPanel) {
        rightPanel.addEventListener("scroll", handleScroll);
      }

      return () => {
        if (rightPanel) {
          rightPanel.removeEventListener("scroll", handleScroll);
        }
      };
    }, [isLoading, hasMore, rightPanelRef, fetchComponents]);

    // Update handleSectionClick to reset both tag states
    const handleSectionClickCb = useCallback(
      (sectionId: CommunityPanelSections) => {
        if (sectionId === selectedSection) return;
        setSelectedSection(sectionId);
        setSelectedLayout(null);
        setSelectedCollections([]);
        setSelectedCategory(null);
        setLeftPanelFilter("");
        handleFilterChange();
      },
      [
        selectedSection,
        setSelectedSection,
        setSelectedLayout,
        setSelectedCollections,
        setSelectedCategory,
        setLeftPanelFilter,
        handleFilterChange,
      ],
    );

    const sectionButtons = useMemo(
      () =>
        sections.map((section) => (
          <Button
            key={section.id}
            variant="ghost"
            className={cn(
              "flex gap-1.5 justify-start text-xs text-left text-secondary-n-500 hover:bg-secondary-n-700 px-2 py-1 max-h-7",
              selectedSection === section.id ? "text-secondary-n-50" : "",
            )}
            onClick={() => handleSectionClickCb(section.id)}
          >
            <Sparkle className="w-4 h-4" />
            <span className="flex-1 truncate">{section.name}</span>
            {selectedSection === section.id && <Check className="w-4 h-4" />}
          </Button>
        )),
      [selectedSection, handleSectionClickCb],
    );

    const [expandedCategories, setExpandedCategories] = useState<
      Record<string, boolean>
    >({ "Application UI": true, "Marketing UI": true, "Product UI": true });

    const toggleCategoryExpanded = useCallback(
      (category: string) => {
        setExpandedCategories((prev) => ({
          ...prev,
          [category]: !prev[category],
        }));
      },
      [setExpandedCategories],
    );

    const renderLayoutFilters = useMemo(() => {
      return (
        <div className="overflow-y-scroll h-full auto-rows-min w-full shrink-0 scrollbar-hide">
          {/* Featured Section */}
          {selectedSection === CommunityPanelSections.Featured && (
            <FeaturedView
              viewMode={viewMode}
              selectedLayout={selectedLayout}
              setSelectedLayout={setSelectedLayout}
              setSelectedCategory={setSelectedCategory}
              selectedCategory={selectedCategory}
              componentCounts={componentCounts.tags}
              handleFilterChange={handleFilterChange}
              layoutOptions={layoutOptions}
              leftPanelFilter={leftPanelFilter}
            />
          )}

          {/* UI Kits Section */}
          {selectedSection === CommunityPanelSections.UIKits && (
            <UIKitsView
              viewMode={viewMode}
              collections={collections}
              selectedCollections={selectedCollections}
              setSelectedCollections={setSelectedCollections}
              componentCounts={componentCounts.collections}
              handleFilterChange={handleFilterChange}
              leftPanelFilter={leftPanelFilter}
            />
          )}

          {/* Categories Section */}
          {selectedSection === CommunityPanelSections.Categories && (
            <CategoryView
              viewMode={viewMode}
              toggleCategoryExpanded={toggleCategoryExpanded}
              expandedCategories={expandedCategories}
              setSelectedCategory={setSelectedCategory}
              selectedCategory={selectedCategory}
              componentCounts={componentCounts}
              handleFilterChange={handleFilterChange}
              leftPanelFilter={leftPanelFilter}
            />
          )}
        </div>
      );
    }, [
      selectedSection,
      viewMode,
      selectedLayout,
      selectedCategory,
      componentCounts,
      handleFilterChange,
      leftPanelFilter,
      expandedCategories,
    ]);

    const handleRightPanelSearchKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          handleFilterChange();
        }
      },
      [handleFilterChange],
    );

    const renderRightPanel = useMemo(() => {
      return (
        <div className="w-full h-full flex flex-col">
          {/* search components */}
          <div className="p-4 flex items-center gap-2">
            <SearchInput
              value={searchVal}
              onChange={handleSearchChange}
              onKeyDown={handleRightPanelSearchKeyDown}
              onClear={handleClearSearch}
              placeholder="Search React Components"
            />
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hide">
            {isLoading && components.length === 0 ? (
              <div className="flex justify-center items-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              </div>
            ) : components.length === 0 ? (
              <div className="text-text-primary text-xs p-4 text-center">
                {searchVal
                  ? `0 results for "${searchVal}"`
                  : "No components found"}
              </div>
            ) : (
              <div
                ref={rightPanelRef}
                className="gap-2 p-4 auto-rows-max grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] scrollbar-hide"
              >
                {components.map((component) => (
                  <div key={component.id} className="content-center">
                    <ComponentAssetCard
                      component={component}
                      onSelect={() => {
                        if (pendingApprovalEnabled) {
                          const tagParams = component.tags?.length
                            ? component.tags
                                .filter((t) => t.name)
                                .map((t) => t.name)
                                .join(",")
                            : undefined;
                          console.log(
                            `http://reactcomponents.com/?id=${component.id}${
                              tagParams ? "&tags=" + tagParams : ""
                            }`,
                            {
                              component,
                            },
                          );
                        }
                      }}
                    />
                  </div>
                ))}
                {isLoading && (
                  <div className="col-span-full flex justify-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
                  </div>
                )}
              </div>
            )}
          </div>
          {/* Kept the browse button at the bottom */}
          <div className="bg-background-primary flex justify-center pointer-events-none h-[60px] items-center p-4 shrink-0">
            <Button
              variant="primary"
              size="sm"
              className="min-h-8 w-full truncate min-w-max pointer-events-auto"
              style={{
                boxShadow: "0 -1rem 1rem rgba(0,0,0,0.1)", // Adjusted shadow slightly
              }}
              onClick={() => {
                window.open("https://reactcomponents.com/", "_blank");
              }}
            >
              <span className="hidden sm:inline">Browse React Components</span>
            </Button>
          </div>
        </div>
      );
    }, [
      isLoading,
      components,
      pendingApprovalEnabled,
      searchVal,
      handleRightPanelSearchKeyDown,
    ]);

    const handleLeftPanelFilterChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setLeftPanelFilter(e.target.value);
      },
      [],
    );

    const handleLeftPanelFilterKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
          handleFilterChange();
        }
      },
      [handleFilterChange],
    );

    const clearLeftPanelFilter = useCallback(() => {
      setLeftPanelFilter("");
      handleFilterChange();
    }, [setLeftPanelFilter, handleFilterChange]);

    return (
      <div className="w-full h-full flex flex-col bg-secondary-0 select-none">
        <span className="border-b border-secondary-300 flex flex-row flex-1 min-h-0">
          <span className="h-full flex flex-col min-w-[230px] w-1/3 border-r border-secondary-300">
            <div className="p-4 flex-col gap-2 space-y-2">
              <div className="flex flex-row gap-2">
                <SearchInput
                  value={leftPanelFilter}
                  onChange={handleLeftPanelFilterChange}
                  onKeyDown={handleLeftPanelFilterKeyDown}
                  onClear={clearLeftPanelFilter}
                />
                <CommunityFilterPopover onFilterChange={handleFilterChange}>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "h-7 w-7 rounded-sm bg-secondary-n-950 hover:bg-secondary-n-800",
                      (selectedCollections.length > 0 ||
                        selectedCategory ||
                        selectedTags.length > 0) && // Check all filters
                        "text-text-primary",
                      isFilterPopoverOpen && "bg-background-tertiary",
                    )}
                  >
                    <ListFilter className="h-4 w-4" />
                  </Button>
                </CommunityFilterPopover>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-7 w-7 rounded-sm bg-secondary-n-950 border border-secondary-300 hover:bg-secondary-n-800",
                  )}
                  onClick={toggleViewMode}
                  aria-label={
                    viewMode === ViewMode.List
                      ? "Switch to Grid View"
                      : "Switch to List View"
                  }
                >
                  {viewMode === ViewMode.List ? (
                    <LayoutGrid className="h-4 w-4" />
                  ) : (
                    <List className="h-4 w-4" />
                  )}
                </Button>
              </div>
              {(selectedCollections.length > 0 ||
                selectedCategory ||
                selectedTags.length > 0 ||
                selectedLayout) && <FilterTags />}
            </div>

            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex flex-col border-t border-secondary-300 shrink-0 p-4 gap-1">
                {sectionButtons}
              </div>
              <div className="border-t border-secondary-300 overflow-y-scroll scrollbar-hide flex-1">
                {renderLayoutFilters}
              </div>
            </div>
          </span>
          <span className="overflow-y-scroll w-full scrollbar-hide">
            {renderRightPanel}
          </span>
        </span>
      </div>
    );
  },
);

export default CommunityPanel;
